import { useEffect, useRef, useState } from "react";
import RightArrow from "../../../components/SVG";
import { createMember } from "../../../services/memberServices";
import { X, Upload } from "lucide-react";
import { deleteImage, uploadImages } from "../../../services/uploaderServices";
import CustomSelect from "../common/CustomSelect";
import Input from "../common/Input";
import Button from "../common/Button";
import Textarea from "../common/TextArea";
import toast from "react-hot-toast";

const validateRequired = (value) => {
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "boolean") return value === true;
  if (typeof value === "number") return !isNaN(value);
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined;
};

const validationRules = {
  profileInfo: {
    firstName: { required: true },
    lastName: { required: true },
  },
  communication: {
    email: { required: true },
    mobile: { required: true },
  },
};

const validateField = (section, field, value, rules) => {
  if (!rules) return null;
  if (rules.required && !validateRequired(value))
    return "This field is required";
  if (rules.validator && !rules.validator(value)) {
    if (field === "email") return "Invalid email format";
    if (field === "mobile" || field === "telephone" || field === "whatsapp")
      return "Invalid phone number";
    if (field === "zipcode") return "Invalid zipcode";
    if (field === "photo" || field === "companyLogo")
      return "Image is required";
    if (field === "dob") return "Date of birth is required";
    if (field === "speciesOfInterest") return "Please select 1-3 species";
    if (field === "fieldOfWork") return "Please select 1-3 fields";
    if (field === "natureOfWork") return "Please select 1-3 options";
    if (field === "agreedToTerms") return "You must agree to the terms";
    if (field === "experienceYears")
      return "Please enter valid years of experience";
    return "Invalid value";
  }
  return null;
};

const validateForm = (formData) => {
  const errors = {};

  Object.entries(validationRules).forEach(([section, rules]) => {
    if (Array.isArray(rules)) {
      formData[section].forEach((item, index) => {
        Object.entries(rules[index]).forEach(([field, fieldRules]) => {
          const error = validateField(
            `${section}.${index}`,
            field,
            item[field],
            fieldRules
          );
          if (error) errors[`${section}.${index}.${field}`] = error;
        });
      });
    } else if (typeof rules === "object") {
      Object.entries(rules).forEach(([field, fieldRules]) => {
        const error = validateField(
          section,
          field,
          formData[section][field],
          fieldRules
        );
        if (error) errors[`${section}.${field}`] = error;
      });
    }
  });

  return errors;
};

const AddForm = () => {
  useEffect(() => {
    if (window.$) {
      window.$("select").niceSelect();
    }
  }, []);

  const initialFormData = {
    profileInfo: {
      title: "Mr.",
      firstName: "",
      lastName: "",
      dob: "",
      gender: "Male",
      photo: "",
    },
    communication: {
      email: "",
      telephone: "",
      whatsapp: "",
      mobile: "",
      address: "",
      city: "",
      zipcode: "",
      country: "IN",
      socialLinks: ["", "", ""],
    },
    organization: {
      organizationName: "",
      organizationType: "Private Enterprise",
      organizationAddress: "",
      organizationPhone: "",
      organizationEmail: "",
      aboutCompany: "",
      companyLogo: "",
    },
    workExperience: {
      experienceYears: 0,
      experienceMonths: 0,
    },
    interests: {
      speciesOfInterest: [],
      fieldOfWork: [],
      natureOfWork: [],
      speciesOther: "",
      fieldOther: "",
      natureOther: "",
    },
    educationDetails: "",
    recommendations: [
      {
        name: "",
        membershipNumber: "",
        companyName: "",
        mobile: "",
        email: "",
      },
      {
        name: "",
        membershipNumber: "",
        companyName: "",
        mobile: "",
        email: "",
      },
    ],
    memberRole: "Lifetime Member",
    memberDesignation: "6896f0a379b374d7da63d536",
    year: new Date().getFullYear().toString(),
    agreedToTerms: false,
    isEmailVerified: true,
    isMobileVerified: true,
    membershipId: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileOne, setFileOne] = useState("No file chosen");
  const [fileTwo, setFileTwo] = useState("No file chosen");

  const [othersState, setOthersState] = useState({
    speciesOfInterest: false,
    fieldOfWork: false,
    natureOfWork: false,
  });

  const fileOneRef = useRef(null);
  const fileTwoRef = useRef(null);

  const titleOptions = [
    { value: "Mr.", label: "Mr." },
    { value: "Ms.", label: "Ms." },
    { value: "Dr.", label: "Dr." },
  ];

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
  ];

  const organizationTypeOptions = [
    { value: "Private Enterprise", label: "Private Enterprise" },
    { value: "Educational Institution", label: "Educational Institution" },
    { value: "Research Organization", label: "Research Organization" },
    { value: "Government", label: "Government" },
    { value: "Others", label: "Others" },
  ];

  const countryOptions = [
    { value: "IN", label: "India" },
    { value: "AF", label: "Afghanistan" },
    { value: "AR", label: "Argentina" },
    { value: "AU", label: "Australia" },
    { value: "BD", label: "Bangladesh" },
    { value: "BR", label: "Brazil" },
    { value: "CA", label: "Canada" },
    { value: "CN", label: "China" },
    { value: "EG", label: "Egypt" },
    { value: "FR", label: "France" },
    { value: "DE", label: "Germany" },
    { value: "ID", label: "Indonesia" },
    { value: "IR", label: "Iran" },
    { value: "IQ", label: "Iraq" },
    { value: "IT", label: "Italy" },
    { value: "JP", label: "Japan" },
  ];

  const handleChange = (value, section, field) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
    if (errors[`${section}.${field}`]) {
      setErrors((prev) => ({ ...prev, [`${section}.${field}`]: undefined }));
    }
  };

  const handleSelectChange = (selectedOption, section, field) => {
    handleChange(selectedOption?.value || "", section, field);
  };

  const handleArrayChange = (value, section, field, index) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].map((item, i) =>
          i === index ? value : item
        ),
      },
    }));
  };

  const handleCheckboxChange = (e, section, field, value) => {
    const { checked } = e.target;

    if (value === "Others") {
      setOthersState((prev) => ({
        ...prev,
        [field]: checked,
      }));

      const otherField =
        field === "speciesOfInterest"
          ? "speciesOther"
          : field === "fieldOfWork"
          ? "fieldOther"
          : "natureOther";

      if (!checked) {
        setFormData((prev) => ({
          ...prev,
          interests: {
            ...prev.interests,
            [otherField]: "",
          },
        }));
      }
    } else {
      setFormData((prev) => {
        const currentArray = prev[section][field];
        let updatedArray;
        if (checked) {
          updatedArray = currentArray.includes(value)
            ? currentArray
            : [...currentArray, value];
        } else {
          updatedArray = currentArray.filter((item) => item !== value);
        }

        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: updatedArray,
          },
        };
      });
    }

    if (errors[`${section}.${field}`]) {
      setErrors((prev) => ({ ...prev, [`${section}.${field}`]: undefined }));
    }
  };

  const handleOtherInputChange = (value, otherField) => {
    setFormData((prev) => ({
      ...prev,
      interests: {
        ...prev.interests,
        [otherField]: value,
      },
    }));
  };

  const getCleanedInterestsData = () => {
    const cleanedInterests = {
      speciesOfInterest: [
        ...formData.interests.speciesOfInterest,
        ...(othersState.speciesOfInterest &&
        formData.interests.speciesOther.trim()
          ? formData.interests.speciesOther
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item)
          : []),
      ].filter((item, index, self) => self.indexOf(item) === index),

      fieldOfWork: [
        ...formData.interests.fieldOfWork,
        ...(othersState.fieldOfWork && formData.interests.fieldOther.trim()
          ? formData.interests.fieldOther
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item)
          : []),
      ].filter((item, index, self) => self.indexOf(item) === index),

      natureOfWork: [
        ...formData.interests.natureOfWork,
        ...(othersState.natureOfWork && formData.interests.natureOther.trim()
          ? formData.interests.natureOther
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item)
          : []),
      ].filter((item, index, self) => self.indexOf(item) === index),
    };

    return cleanedInterests;
  };

  const handleRecommendationChange = (value, index, field) => {
    setFormData((prev) => ({
      ...prev,
      recommendations: prev.recommendations.map((rec, i) =>
        i === index ? { ...rec, [field]: value } : rec
      ),
    }));
    if (errors[`recommendations.${index}.${field}`]) {
      setErrors((prev) => ({
        ...prev,
        [`recommendations.${index}.${field}`]: undefined,
      }));
    }
  };

  const handleEducationDetailsChange = (value) => {
    setFormData((prev) => ({ ...prev, educationDetails: value }));
    if (errors.educationDetails) {
      setErrors((prev) => ({ ...prev, educationDetails: undefined }));
    }
  };

  const handleFileOneChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileOne("Uploading...");
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("images", file);
      const response = await uploadImages(formDataUpload);
      const imageUrl = response.images[0].imageUrl;
      setFileOne(file.name);
      setFormData((prev) => ({
        ...prev,
        profileInfo: { ...prev.profileInfo, photo: imageUrl },
      }));
      if (errors["profileInfo.photo"]) {
        setErrors((prev) => ({ ...prev, "profileInfo.photo": undefined }));
      }
    } catch (error) {
      console.error("Error uploading photo:", error);
      setFileOne("Upload failed");
    } finally {
      if (fileOneRef.current) fileOneRef.current.value = "";
    }
  };

  const handleDeletePhoto = async () => {
    try {
      if (formData.profileInfo.photo) {
        await deleteImage(formData.profileInfo.photo);
      }
      setFileOne("No file chosen");
      setFormData((prev) => ({
        ...prev,
        profileInfo: { ...prev.profileInfo, photo: "" },
      }));
    } catch (error) {
      console.error("Error deleting photo:", error);
    } finally {
      if (fileOneRef.current) fileOneRef.current.value = "";
    }
  };

  const handleFileTwoChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileTwo("Uploading...");
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("images", file);
      const response = await uploadImages(formDataUpload);
      const imageUrl = response.images[0].imageUrl;
      setFileTwo(file.name);
      setFormData((prev) => ({
        ...prev,
        organization: { ...prev.organization, companyLogo: imageUrl },
      }));
      if (errors["organization.companyLogo"]) {
        setErrors((prev) => ({
          ...prev,
          "organization.companyLogo": undefined,
        }));
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      setFileTwo("Upload failed");
    } finally {
      if (fileTwoRef.current) fileTwoRef.current.value = "";
    }
  };

  const handleDeleteLogo = async () => {
    try {
      if (formData.organization.companyLogo) {
        await deleteImage(formData.organization.companyLogo);
      }
      setFileTwo("No file chosen");
      setFormData((prev) => ({
        ...prev,
        organization: { ...prev.organization, companyLogo: "" },
      }));
    } catch (error) {
      console.error("Error deleting logo:", error);
    } finally {
      if (fileTwoRef.current) fileTwoRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanedInterests = getCleanedInterestsData();
    const validationData = {
      ...formData,
      interests: cleanedInterests,
    };

    const validationErrors = validateForm(validationData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      const firstErrorKey = Object.keys(validationErrors)[0];
      const firstErrorElement = document.querySelector(
        `[name="${firstErrorKey}"]`
      );
      firstErrorElement?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        interests: cleanedInterests,
        profileInfo: {
          ...formData.profileInfo,
          dob: formData.profileInfo.dob
            ? new Date(formData.profileInfo.dob)
            : null,
        },
        membershipType: "standard",
        status: "approved",
      };

      delete payload.interests.speciesOther;
      delete payload.interests.fieldOther;
      delete payload.interests.natureOther;

      //  send membershipId only if not empty
      if (formData.membershipId.trim() === "") {
        delete payload.membershipId;
      }

      await createMember(payload);
      toast.success("Member added successfully");

      setFormData(initialFormData);
      setFileOne("No file chosen");
      setFileTwo("No file chosen");
      setOthersState({
        speciesOfInterest: false,
        fieldOfWork: false,
        natureOfWork: false,
      });
      setErrors({});
      window.location.href = "/admin/members/list";
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen">
      <div className="container mx-auto py-8">
        <div className="">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-1">
                  <CustomSelect
                    label="Title"
                    value={titleOptions.find(
                      (opt) => opt.value === formData.profileInfo.title
                    )}
                    onChange={(option) =>
                      handleSelectChange(option, "profileInfo", "title")
                    }
                    options={titleOptions}
                    isClearable={false}
                  />
                </div>

                <div className="lg:col-span-1">
                  <Input
                    label="First Name"
                    value={formData.profileInfo.firstName}
                    onChange={(value) =>
                      handleChange(value, "profileInfo", "firstName")
                    }
                    error={errors["profileInfo.firstName"]}
                  />
                </div>

                <div className="lg:col-span-1">
                  <Input
                    label="Last Name"
                    value={formData.profileInfo.lastName}
                    onChange={(value) =>
                      handleChange(value, "profileInfo", "lastName")
                    }
                    error={errors["profileInfo.lastName"]}
                  />
                </div>

                <Input
                  label="Membership ID (optional)"
                  value={formData.membershipId}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      membershipId: value,
                    }))
                  }
                />

                <div className="lg:col-span-2">
                  <Input
                    label="Date of Birth"
                    type="date"
                    value={formData.profileInfo.dob}
                    onChange={(value) =>
                      handleChange(value, "profileInfo", "dob")
                    }
                  />
                </div>

                <div className="lg:col-span-2">
                  <CustomSelect
                    label="Gender"
                    value={genderOptions.find(
                      (opt) => opt.value === formData.profileInfo.gender
                    )}
                    onChange={(option) =>
                      handleSelectChange(option, "profileInfo", "gender")
                    }
                    options={genderOptions}
                    isClearable={false}
                  />
                </div>

                <div className="lg:col-span-4">
                  <label className="block text-base font-semibold text-black mb-2">
                    Photo
                  </label>
                  <div className="flex items-center gap-4 mb-4">
                    <input
                      id="fileOne"
                      type="file"
                      hidden
                      ref={fileOneRef}
                      accept="image/*"
                      onChange={handleFileOneChange}
                    />
                    <Button
                      variant="secondary"
                      onClick={() => fileOneRef.current?.click()}
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <Upload size={16} />
                      Choose File
                    </Button>
                    <span className="text-sm text-gray-600">{fileOne}</span>
                  </div>
                  {formData.profileInfo.photo && (
                    <div className="relative inline-block">
                      <img
                        src={formData.profileInfo.photo}
                        alt="Profile Preview"
                        className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                      />
                      <Button
                        onClick={handleDeletePhoto}
                        variant="secondary"
                        size="sm"
                        className="absolute -top-2 -right-2 !p-1 bg-red-500 hover:bg-red-600 text-white border-0 rounded-full"
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Communication Details Section */}
            <div className="mb-8">
              <div className="border-b border-gray-200 pb-4 mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Communication Details
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Input
                    label="Email ID"
                    type="email"
                    value={formData.communication.email}
                    onChange={(value) =>
                      handleChange(value, "communication", "email")
                    }
                    error={errors["communication.email"]}
                    className="mb-2"
                  />
                </div>

                <div>
                  <Input
                    label="Mobile"
                    type="tel"
                    value={formData.communication.mobile}
                    onChange={(value) =>
                      handleChange(value, "communication", "mobile")
                    }
                    error={errors["communication.mobile"]}
                    className="mb-2"
                  />
                </div>

                <div>
                  <Input
                    label="Telephone"
                    type="tel"
                    value={formData.communication.telephone}
                    onChange={(value) =>
                      handleChange(value, "communication", "telephone")
                    }
                  />
                </div>

                <div>
                  <Input
                    label="WhatsApp Number"
                    type="tel"
                    value={formData.communication.whatsapp}
                    onChange={(value) =>
                      handleChange(value, "communication", "whatsapp")
                    }
                  />
                </div>

                <div>
                  <Input
                    label="Address"
                    value={formData.communication.address}
                    onChange={(value) =>
                      handleChange(value, "communication", "address")
                    }
                  />
                </div>

                <div>
                  <Input
                    label="City"
                    value={formData.communication.city}
                    onChange={(value) =>
                      handleChange(value, "communication", "city")
                    }
                  />
                </div>

                <div>
                  <Input
                    label="Zipcode"
                    value={formData.communication.zipcode}
                    onChange={(value) =>
                      handleChange(value, "communication", "zipcode")
                    }
                  />
                </div>

                <div>
                  <CustomSelect
                    label="Country"
                    value={countryOptions.find(
                      (opt) => opt.value === formData.communication.country
                    )}
                    onChange={(option) =>
                      handleSelectChange(option, "communication", "country")
                    }
                    options={countryOptions}
                    isClearable={false}
                  />
                </div>

                <div className="md:col-span-2">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Website/Social Media"
                      value={formData.communication.socialLinks[0]}
                      onChange={(value) =>
                        handleArrayChange(
                          value,
                          "communication",
                          "socialLinks",
                          0
                        )
                      }
                    />
                    <Input
                      label="Website/Social Media"
                      value={formData.communication.socialLinks[1]}
                      onChange={(value) =>
                        handleArrayChange(
                          value,
                          "communication",
                          "socialLinks",
                          1
                        )
                      }
                    />
                    <Input
                      label="Website/Social Media"
                      value={formData.communication.socialLinks[2]}
                      onChange={(value) =>
                        handleArrayChange(
                          value,
                          "communication",
                          "socialLinks",
                          2
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Organizational Details Section */}
            <div className="mb-8">
              <div className="border-b border-gray-200 pb-4 mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Organizational Details
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Input
                    label="Organization Name"
                    value={formData.organization.organizationName}
                    onChange={(value) =>
                      handleChange(value, "organization", "organizationName")
                    }
                  />
                </div>

                <div>
                  <CustomSelect
                    label="Organization Type"
                    value={organizationTypeOptions.find(
                      (opt) =>
                        opt.value === formData.organization.organizationType
                    )}
                    onChange={(option) =>
                      handleSelectChange(
                        option,
                        "organization",
                        "organizationType"
                      )
                    }
                    options={organizationTypeOptions}
                    isClearable={false}
                  />
                </div>

                <div className="col-span-2">
                  <Textarea
                    label="Address"
                    value={formData.organization.organizationAddress}
                    onChange={(e) =>
                      handleChange(
                        e.target.value,
                        "organization",
                        "organizationAddress"
                      )
                    }
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 col-span-2">
                  <Input
                    label="Phone"
                    type="tel"
                    value={formData.organization.organizationPhone}
                    onChange={(value) =>
                      handleChange(value, "organization", "organizationPhone")
                    }
                  />

                  <Input
                    label="Email"
                    type="email"
                    value={formData.organization.organizationEmail}
                    onChange={(value) =>
                      handleChange(value, "organization", "organizationEmail")
                    }
                  />
                </div>

                <div className="md:col-span-2">
                  <Textarea
                    label="About Company"
                    value={formData.organization.aboutCompany}
                    onChange={(e) =>
                      handleChange(
                        e.target.value,
                        "organization",
                        "aboutCompany"
                      )
                    }
                    rows={4}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-base font-semibold text-black mb-2">
                    Company Logo
                  </label>
                  <div className="flex items-center gap-4 mb-4">
                    <input
                      id="fileTwo"
                      type="file"
                      hidden
                      ref={fileTwoRef}
                      accept="image/*"
                      onChange={handleFileTwoChange}
                    />
                    <Button
                      variant="secondary"
                      onClick={() => fileTwoRef.current?.click()}
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <Upload size={16} />
                      Choose File
                    </Button>
                    <span className="text-sm text-gray-600">{fileTwo}</span>
                  </div>

                  {formData.organization.companyLogo && (
                    <div className="relative inline-block">
                      <img
                        src={formData.organization.companyLogo}
                        alt="Company Logo Preview"
                        className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                      />
                      <Button
                        onClick={handleDeleteLogo}
                        variant="secondary"
                        size="sm"
                        className="absolute -top-2 -right-2 !p-1 bg-red-500 hover:bg-red-600 text-white border-0 rounded-full"
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-8">
              <div className="border-b border-gray-200 pb-4 mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Work Experience
                </h2>
                <p className="text-gray-600 text-sm mt-2">
                  Provide details such as organization name, your job title
                  and/or responsibilities, and number of years and months in
                  each job in the following. Please start with your current
                  position.
                </p>
                <h6 className="text-base font-semibold text-gray-700 mt-4">
                  Year of Start in Work Experience in Aquaculture
                </h6>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Input
                    label="Years"
                    type="number"
                    value={formData.workExperience.experienceYears}
                    onChange={(value) =>
                      handleChange(
                        parseInt(value) || 0,
                        "workExperience",
                        "experienceYears"
                      )
                    }
                  />
                </div>

                <div>
                  <Input
                    label="Months (Optional)"
                    type="number"
                    value={formData.workExperience.experienceMonths}
                    onChange={(value) =>
                      handleChange(
                        parseInt(value) || 0,
                        "workExperience",
                        "experienceMonths"
                      )
                    }
                  />
                </div>
              </div>
            </div>

            {/* Species of Interest Section */}
            <div className="mb-8">
              <div className="border-b border-gray-200 pb-4 mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Species of Interest
                </h2>
                <p className="text-gray-600 text-sm mt-2">
                  (Required Minimum one and maximum 3 areas can be selected)
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {[
                  "Shrimp",
                  "Warm Water Freshwater Fish",
                  "Coldwater Fish",
                  "Marine Fish",
                ].map((species) => (
                  <label
                    key={species}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.interests.speciesOfInterest.includes(
                        species
                      )}
                      onChange={(e) =>
                        handleCheckboxChange(
                          e,
                          "interests",
                          "speciesOfInterest",
                          species
                        )
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{species}</span>
                  </label>
                ))}
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={othersState.speciesOfInterest}
                    onChange={(e) =>
                      handleCheckboxChange(
                        e,
                        "interests",
                        "speciesOfInterest",
                        "Others"
                      )
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Others</span>
                </label>
              </div>

              <div className="max-w-md mb-4">
                <Input
                  placeholder="Others (comma separated)"
                  value={formData.interests.speciesOther}
                  onChange={(value) =>
                    handleOtherInputChange(value, "speciesOther")
                  }
                  disabled={!othersState.speciesOfInterest}
                />
              </div>
            </div>

            {/* Field of Work Section */}
            <div className="mb-8">
              <div className="border-b border-gray-200 pb-4 mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Field of Work
                </h2>
                <p className="text-gray-600 text-sm mt-2">
                  (Required Minimum one and maximum 3 areas can be selected)
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {[
                  "Hatchery Sector",
                  "Farm Sector",
                  "Healthcare Sector",
                  "Feed Sector",
                  "Machinery Sector",
                  "Government Sector",
                  "Processing Sector",
                  "Fish Farming & Allied Sector",
                  "Markets, Certifications, Policy",
                  "Media",
                ].map((field) => (
                  <label
                    key={field}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.interests.fieldOfWork.includes(field)}
                      onChange={(e) =>
                        handleCheckboxChange(
                          e,
                          "interests",
                          "fieldOfWork",
                          field
                        )
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{field}</span>
                  </label>
                ))}
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={othersState.fieldOfWork}
                    onChange={(e) =>
                      handleCheckboxChange(
                        e,
                        "interests",
                        "fieldOfWork",
                        "Others"
                      )
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Others</span>
                </label>
              </div>

              <div className="max-w-md mb-4">
                <Input
                  placeholder="Others (comma separated)"
                  value={formData.interests.fieldOther}
                  onChange={(value) =>
                    handleOtherInputChange(value, "fieldOther")
                  }
                  disabled={!othersState.fieldOfWork}
                />
              </div>
            </div>

            {/* Nature of Work Section */}
            <div className="mb-8">
              <div className="border-b border-gray-200 pb-4 mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Nature of Work
                </h2>
                <p className="text-gray-600 text-sm mt-2">
                  (Required Minimum one and maximum 3 areas can be selected)
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {[
                  "Teaching",
                  "Research",
                  "Production",
                  "Management",
                  "Policy",
                ].map((nature) => (
                  <label
                    key={nature}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.interests.natureOfWork.includes(nature)}
                      onChange={(e) =>
                        handleCheckboxChange(
                          e,
                          "interests",
                          "natureOfWork",
                          nature
                        )
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{nature}</span>
                  </label>
                ))}
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={othersState.natureOfWork}
                    onChange={(e) =>
                      handleCheckboxChange(
                        e,
                        "interests",
                        "natureOfWork",
                        "Others"
                      )
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Others</span>
                </label>
              </div>

              <div className="max-w-md mb-4">
                <Input
                  placeholder="Others (comma separated)"
                  value={formData.interests.natureOther}
                  onChange={(value) =>
                    handleOtherInputChange(value, "natureOther")
                  }
                  disabled={!othersState.natureOfWork}
                />
              </div>
            </div>

            {/* Education Details Section */}
            <div className="mb-8">
              <div className="border-b border-gray-200 pb-4 mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Education Details
                </h2>
              </div>

              <Textarea
                label="Education Details"
                value={formData.educationDetails}
                onChange={(e) => handleEducationDetailsChange(e.target.value)}
                rows={4}
              />
            </div>

            {/* Recommendation for Membership Section */}
            <div className="mb-8">
              <div className="border-b border-gray-200 pb-4 mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Recommendation for Membership
                </h2>
                <p className="text-gray-600 text-sm mt-2">
                  Each application for membership should be supported by two
                  members of Society of Aquaculture Professionals. Please fill
                  up the following
                </p>
              </div>

              {/* Recommendation 1 */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Recommendation 1
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Input
                    label="Name"
                    value={formData.recommendations[0].name}
                    onChange={(value) =>
                      handleRecommendationChange(value, 0, "name")
                    }
                  />
                  <Input
                    label="Membership Number"
                    value={formData.recommendations[0].membershipNumber}
                    onChange={(value) =>
                      handleRecommendationChange(value, 0, "membershipNumber")
                    }
                  />
                  <Input
                    label="Company Name"
                    value={formData.recommendations[0].companyName}
                    onChange={(value) =>
                      handleRecommendationChange(value, 0, "companyName")
                    }
                  />
                  <Input
                    label="Mobile Number"
                    type="tel"
                    value={formData.recommendations[0].mobile}
                    onChange={(value) =>
                      handleRecommendationChange(value, 0, "mobile")
                    }
                  />
                  <Input
                    label="Email ID"
                    type="email"
                    value={formData.recommendations[0].email}
                    onChange={(value) =>
                      handleRecommendationChange(value, 0, "email")
                    }
                  />
                </div>
              </div>

              {/* Recommendation 2 */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Recommendation 2
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Input
                    label="Name"
                    value={formData.recommendations[1].name}
                    onChange={(value) =>
                      handleRecommendationChange(value, 1, "name")
                    }
                  />
                  <Input
                    label="Membership Number"
                    value={formData.recommendations[1].membershipNumber}
                    onChange={(value) =>
                      handleRecommendationChange(value, 1, "membershipNumber")
                    }
                  />
                  <Input
                    label="Company Name"
                    value={formData.recommendations[1].companyName}
                    onChange={(value) =>
                      handleRecommendationChange(value, 1, "companyName")
                    }
                  />
                  <Input
                    label="Mobile Number"
                    type="tel"
                    value={formData.recommendations[1].mobile}
                    onChange={(value) =>
                      handleRecommendationChange(value, 1, "mobile")
                    }
                  />
                  <Input
                    label="Email ID"
                    type="email"
                    value={formData.recommendations[1].email}
                    onChange={(value) =>
                      handleRecommendationChange(value, 1, "email")
                    }
                  />
                </div>
              </div>
            </div>

            {/* Submit Section */}
            <div className="border-t border-gray-200 pt-8">
              <div className="flex flex-col items-center">
                <Button
                  onClick={handleSubmit}
                  variant="primary"
                  size="lg"
                  disabled={isSubmitting}
                  className="px-8 py-3 flex items-center gap-2"
                >
                  {isSubmitting ? "Creating..." : "Add New Member"}
                  {!isSubmitting && <RightArrow />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default AddForm;
